/**
 * Dashboard específico para BARBEROS
 * - Solo ve su sucursal
 * - KPIs: Turnos pendientes, cortes realizados hoy
 * - Acciones rápidas: Gestionar cola, ver mis citas
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { branchService } from '@/services/branchService';
import { getBarberByUserId } from '@/services/barberService';
import type { Branch } from '@/types';
import {
  Users,
  Clock,
  CheckCircle,
  TrendingUp,
  MapPin,
  Calendar,
  Play,
  ListOrdered,
  History,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface BarberStats {
  pendingTickets: number;
  completedToday: number;
  inServiceNow: number;
  totalWaiting: number;
}

interface CompletedService {
  id: string;
  clientName: string;
  serviceName?: string;
  completedAt: Timestamp;
  actualServiceDuration?: number;
}

export function BarberDashboard() {
  const { t } = useTranslation();
  const { user, firebaseUser, customClaims, loading: authLoading } = useAuth();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [stats, setStats] = useState<BarberStats>({
    pendingTickets: 0,
    completedToday: 0,
    inServiceNow: 0,
    totalWaiting: 0,
  });
  const [recentServices, setRecentServices] = useState<CompletedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [barberId, setBarberId] = useState<string | null>(null);

  const branchId = user?.branchId || customClaims?.branchId;

  // Fetch the correct barberId from the barbers collection
  useEffect(() => {
    async function fetchBarberId() {
      if (authLoading || !firebaseUser?.uid) {
        setBarberId(null);
        return;
      }

      try {
        const barber = await getBarberByUserId(firebaseUser.uid);
        if (barber) {
          setBarberId(barber.barberId);
          console.log('✅ BarberDashboard: Barber found', {
            userId: firebaseUser.uid,
            barberId: barber.barberId,
          });
        } else {
          console.warn('⚠️ BarberDashboard: No barber document found for userId:', firebaseUser.uid);
          setBarberId(null);
        }
      } catch (error) {
        console.error('❌ BarberDashboard: Error fetching barber:', error);
        setBarberId(null);
      }
    }

    fetchBarberId();
  }, [firebaseUser?.uid, authLoading]);

  useEffect(() => {
    // Wait for auth to finish loading before attempting to load data
    if (authLoading) {
      return;
    }

    // Only load data if we have a barberId
    if (!barberId) {
      setLoading(false);
      return;
    }

    loadData();

    // Reload stats every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [barberId, branchId, authLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBranch(),
        loadStats(),
        loadRecentServices(),
      ]);
    } catch (error) {
      console.error('Error loading barber dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranch = async () => {
    if (!branchId) return;

    try {
      const branchData = await branchService.getById(branchId);
      setBranch(branchData);
    } catch (error) {
      console.error('Error loading branch:', error);
    }
  };

  const loadStats = async () => {
    if (!barberId) return;

    try {
      // Get today's start (00:00)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      // Pending tickets (waiting, notified, arrived)
      const pendingQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', 'in', ['waiting', 'notified', 'arrived'])
      );
      const pendingSnapshot = await getDocs(pendingQuery);

      // In service now
      const inServiceQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'in_service')
      );
      const inServiceSnapshot = await getDocs(inServiceQuery);

      // Completed today
      const completedQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'completed'),
        where('completedAt', '>=', todayTimestamp)
      );
      const completedSnapshot = await getDocs(completedQuery);

      setStats({
        pendingTickets: pendingSnapshot.size,
        completedToday: completedSnapshot.size,
        inServiceNow: inServiceSnapshot.size,
        totalWaiting: pendingSnapshot.size + inServiceSnapshot.size,
      });
    } catch (error) {
      console.error('Error loading barber stats:', error);
    }
  };

  const loadRecentServices = async () => {
    if (!barberId) return;

    try {
      // Get last 10 completed services (any day)
      const recentQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'completed'),
        // orderBy('completedAt', 'desc'), // Will need index: barberId + status + completedAt
        // limit(10)
      );

      const recentSnapshot = await getDocs(recentQuery);

      const services: CompletedService[] = recentSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            clientName: data.clientName || 'Cliente',
            serviceName: data.serviceName,
            completedAt: data.completedAt,
            actualServiceDuration: data.actualServiceDuration,
          };
        })
        .filter((s) => s.completedAt) // Only services with completedAt
        .sort((a, b) => b.completedAt.toMillis() - a.completedAt.toMillis()) // Sort by most recent
        .slice(0, 10); // Get last 10

      setRecentServices(services);
    } catch (error) {
      console.error('Error loading recent services:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-ink-subtle animate-spin mx-auto mb-4" aria-hidden="true" />
          <p className="text-ink-muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-ink mb-1">
          {t('dashboard.greeting', { name: user?.displayName || 'Barbero' })}
        </h1>
        <p className="text-sm text-ink-muted">
          Panel de control para gestionar tu cola y citas
        </p>
      </div>

      {/* Branch Info */}
      {branch && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-ink-subtle shrink-0" aria-hidden="true" />
            <div>
              <h2 className="font-sans text-lg font-semibold text-ink">{branch.name}</h2>
              <p className="text-sm text-ink-muted">{branch.address}, {branch.city}</p>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Turnos Pendientes */}
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
            <span className="text-3xl font-semibold text-ink tabular-nums">{stats.totalWaiting}</span>
          </div>
          <h3 className="text-sm text-ink-muted">Turnos Pendientes</h3>
        </div>

        {/* En Servicio Ahora — accent: el dato "en vivo" más relevante */}
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Play className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
            <span className="text-3xl font-semibold text-ink tabular-nums">{stats.inServiceNow}</span>
          </div>
          <h3 className="text-sm text-ink-muted">En Servicio Ahora</h3>
        </div>

        {/* Completados Hoy */}
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
            <span className="text-3xl font-semibold text-ink tabular-nums">{stats.completedToday}</span>
          </div>
          <h3 className="text-sm text-ink-muted">Cortes Realizados Hoy</h3>
        </div>

        {/* En Espera */}
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
            <span className="text-3xl font-semibold text-ink tabular-nums">{stats.pendingTickets}</span>
          </div>
          <h3 className="text-sm text-ink-muted">En Cola de Espera</h3>
        </div>
      </div>

      {/* Acciones Rápidas */}
      <div className="bg-surface border border-hairline rounded-xl p-6">
        <h2 className="font-sans text-lg font-semibold text-ink mb-4">
          Acciones Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Gestionar Mi Cola — acción primaria del barbero (único CTA amarillo) */}
          <Link
            to="/barber-queue"
            className="p-6 bg-accent text-on-accent rounded-xl hover:bg-accent-hover transition-colors"
          >
            <ListOrdered className="w-7 h-7 mb-2" aria-hidden="true" />
            <h3 className="font-sans text-lg font-bold mb-1">Gestionar Cola</h3>
            <p className="text-sm opacity-80">Ver y mover turnos</p>
            {stats.totalWaiting > 0 && (
              <div className="mt-2 inline-block bg-on-accent/10 px-3 py-1 rounded-full text-sm font-bold tabular-nums">
                {stats.totalWaiting} en espera
              </div>
            )}
          </Link>

          {/* Llamar Siguiente */}
          <Link
            to="/barber-queue"
            className="group p-6 bg-surface-2 border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
          >
            <Play className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
            <h3 className="font-sans text-lg font-semibold text-ink mb-1 flex items-center gap-1">
              Llamar Siguiente <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
            </h3>
            <p className="text-sm text-ink-muted">Atender próximo cliente</p>
          </Link>

          {/* Mis Citas */}
          <Link
            to="/appointments"
            className="group p-6 bg-surface-2 border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
          >
            <Calendar className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
            <h3 className="font-sans text-lg font-semibold text-ink mb-1 flex items-center gap-1">
              Mis Citas <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
            </h3>
            <p className="text-sm text-ink-muted">Ver calendario</p>
          </Link>

          {/* Estadísticas */}
          <Link
            to="/profile"
            className="group p-6 bg-surface-2 border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
          >
            <TrendingUp className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
            <h3 className="font-sans text-lg font-semibold text-ink mb-1 flex items-center gap-1">
              Mis Estadísticas <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
            </h3>
            <p className="text-sm text-ink-muted">Rendimiento del mes</p>
          </Link>

          {/* Historial / Contabilidad */}
          <Link
            to="/barber-history"
            className="group p-6 bg-surface-2 border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
          >
            <History className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
            <h3 className="font-sans text-lg font-semibold text-ink mb-1 flex items-center gap-1">
              Historial Completo <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
            </h3>
            <p className="text-sm text-ink-muted">Contabilidad y reportes</p>
          </Link>
        </div>
      </div>

      {/* Recent Activity / Next Clients */}
      {stats.pendingTickets > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <h2 className="font-sans text-lg font-semibold text-ink mb-4">
            Próximos Clientes
          </h2>
          <Link
            to="/barber-queue"
            className="block p-4 bg-surface-2 border border-accent/40 rounded-lg hover:border-accent transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-ink font-medium">
                  Tienes {stats.pendingTickets} cliente{stats.pendingTickets > 1 ? 's' : ''} en espera
                </p>
                <p className="text-sm text-ink-muted">
                  Haz clic para gestionar tu cola
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-accent shrink-0" aria-hidden="true" />
            </div>
          </Link>
        </div>
      )}

      {/* Historial de Servicios Realizados */}
      {recentServices.length > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <History className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            <h2 className="font-sans text-lg font-semibold text-ink">
              Servicios Realizados Recientemente
            </h2>
          </div>
          <div className="space-y-3">
            {recentServices.map((service) => {
              const completedDate = service.completedAt.toDate();
              const timeAgo = Math.floor((Date.now() - completedDate.getTime()) / 1000 / 60); // minutes ago
              let timeAgoStr = '';
              if (timeAgo < 60) {
                timeAgoStr = `Hace ${timeAgo} min`;
              } else if (timeAgo < 1440) {
                timeAgoStr = `Hace ${Math.floor(timeAgo / 60)}h`;
              } else {
                timeAgoStr = completedDate.toLocaleDateString();
              }

              return (
                <div
                  key={service.id}
                  className="p-4 bg-surface-2 rounded-lg border border-hairline"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success" aria-hidden="true" />
                        <p className="font-medium text-ink">
                          {service.clientName}
                        </p>
                      </div>
                      {service.serviceName && (
                        <p className="text-sm text-ink-muted mt-1 ml-6">
                          {service.serviceName}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-ink">
                        {timeAgoStr}
                      </p>
                      {service.actualServiceDuration && (
                        <p className="text-xs text-ink-subtle tabular-nums">
                          {service.actualServiceDuration} min
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
